import type {FC} from 'react';
import {FaGoogle} from 'react-icons/fa';
import {FormattedMessage, useIntl} from 'react-intl';
import type {GoogleLoginProps, GoogleLoginResponse, GoogleLoginResponseOffline} from 'react-google-login';
import GoogleLogin from 'react-google-login';
import {useAuth} from '@/contexts/auth-context';
import {useAlerts} from '@/components/alerts';

type LoginResponse = GoogleLoginResponse | GoogleLoginResponseOffline;

const isLoginResponse = (res: LoginResponse): res is GoogleLoginResponse => 'tokenObj' in res;

const CustomLoginButton: GoogleLoginProps['render'] = ({onClick, disabled}) => (
  <button
    type="button"
    className="flex items-center justify-center bg-blue-500 hover:bg-blue-600 w-full my-4 p-2 rounded-lg text-white"
    onClick={onClick}
    disabled={disabled}
  >
    <FaGoogle size={18} />
    <span className="mx-2">
      <FormattedMessage id="words.continue_with_google" />
    </span>
  </button>
);

const GoogleSignInButton: FC = () => {
  const auth = useAuth();
  const intl = useIntl();
  const alerts = useAlerts();
  const handleGoogleSignIn = async (res: LoginResponse) => {
    try {
      if (isLoginResponse(res)) {
        const profile = res.getBasicProfile();
        const {id_token} = res.getAuthResponse();
        await auth.loginByGoogleAccount({
          name: profile.getName(),
          email: profile.getEmail(),
          avatar_url: profile.getImageUrl(),
          id_token: id_token,
        });
      }
    } catch (err) {
      alerts.error(
        intl.formatMessage({
          id: 'errors.unable_to_login_with_google_account',
        }),
      );
    }
  };

  const handleGoogleSignFailure = () => {
    alerts.error(
      intl.formatMessage({
        id: 'errors.unable_to_login_with_google_account',
      }),
    );
  };

  return (
    <GoogleLogin
      clientId={process.env.NEXT_PUBLIC_FIREBASE_WEB_CLIENT_ID ?? ''}
      render={CustomLoginButton}
      onSuccess={handleGoogleSignIn}
      onFailure={handleGoogleSignFailure}
      cookiePolicy="single_host_origin"
    />
  );
};

export default GoogleSignInButton;
